import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCurrentUser } from '../services/auth';
import { Check, X, ArrowDown, ArrowUp, Edit2, Trash2, Plus, ChevronDown, ChevronRight, Search, RefreshCw } from 'lucide-react';
import { sessionHeaders } from '../services/auth';

const NEON = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/neon'
  : '/.netlify/functions/neon';

async function postNeon(query, params = {}) {
  const res = await fetch(NEON, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ query, params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Neon error ${res.status}`);
  if (data.error) throw new Error(data.error);
  return data;
}

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtMoneyPennies(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; }

// Email domain classifier (frontend; cheap + deterministic)
const PERSONAL_DOMAINS = new Set([
  'gmail.com','googlemail.com','yahoo.com','ymail.com','rocketmail.com','hotmail.com','outlook.com','live.com','msn.com','aol.com',
  'icloud.com','me.com','mac.com','protonmail.com','proton.me','fastmail.com','gmx.com','gmx.de',
  'comcast.net','verizon.net','sbcglobal.net','att.net','cox.net','charter.net','optonline.net','roadrunner.com','rr.com',
  'frontier.com','windstream.net','mindspring.com','earthlink.net','bellsouth.net','pacbell.net','juno.com',
]);
const MEDIA_DOMAINS = new Set([
  'nytimes.com','washingtonpost.com','wsj.com','cnn.com','foxnews.com','nbcuni.com','nbc.com','abc.com','cbs.com','espn.com',
  'bloomberg.net','reuters.com','npr.org','pbs.org','bbc.com','theatlantic.com','newyorker.com','politico.com','huffpost.com',
  'usatoday.com','latimes.com','chicagotribune.com','baltimoresun.com','washingtontimes.com','thehill.com','axios.com','vox.com',
  'vice.com','buzzfeed.com','hearst.com','condenast.com','meredithcorp.com','nationalgeographic.com','forbes.com','fortune.com',
]);
function classifyDomain(domain) {
  if (!domain) return 'unknown';
  const d = domain.toLowerCase();
  if (/\.gov(\.\w+)?$/.test(d)) return 'government';
  if (d.endsWith('.mil')) return 'military';
  if (d.endsWith('.edu')) return 'education';
  if (PERSONAL_DOMAINS.has(d)) return 'personal';
  if (MEDIA_DOMAINS.has(d)) return 'media';
  if (d.endsWith('.org')) return 'nonprofit';
  if (d.endsWith('.com') || d.endsWith('.co') || d.endsWith('.io') || d.endsWith('.net') || d.endsWith('.biz')) return 'corporate';
  return 'other';
}
const DOMAIN_TYPE_COLORS = {
  personal: '#6b7280', corporate: '#0ea5e9', government: '#b91c1c', military: '#7c2d12',
  education: '#15803d', media: '#9333ea', nonprofit: '#ca8a04', other: '#4b5563', unknown: '#9ca3af',
};

const CAT_COLORS = {
  actor: '#8b5cf6', athlete: '#16a34a', musician: '#db2777', politician: '#b91c1c',
  business: '#0ea5e9', tv: '#ea580c', author: '#ca8a04', journalist: '#0d9488', other: '#6b7280',
};
const CONFIDENCE_COLORS = { high: '#16a34a', medium: '#ca8a04', low: '#6b7280' };

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function CustomerCategorization({ navigateTo }) {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('Manager');
  const [tab, setTab] = useState('celebrities');

  if (!isAllowed) {
    return <div className="card" style={{ padding: 24 }}>You need Admin or Manager access to view Categorization.</div>;
  }

  return (
    <div style={{ padding: '0 16px 24px' }}>
      <h1 style={{ margin: '12px 0 4px', fontSize: 22 }}>Customer Categorization</h1>
      <div style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 14 }}>
        Curate notable customers, explore by email domain / spending tier, and apply your own tags.
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--gray-200)', marginBottom: 12 }}>
        {[
          ['celebrities', 'Celebrities'],
          ['domains', 'Email Domains'],
          ['tiers', 'Spending Tiers'],
          ['tags', 'Custom Tags'],
          ['shared', 'Shared Accounts'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              border: 'none', background: 'none', padding: '8px 14px', fontSize: 13, fontWeight: tab === key ? 600 : 400,
              color: tab === key ? 'var(--navy)' : 'var(--gray-500)',
              borderBottom: tab === key ? '2px solid var(--orange)' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer',
            }}
          >{label}</button>
        ))}
      </div>

      {tab === 'celebrities' && <CelebritiesTab navigateTo={navigateTo} />}
      {tab === 'domains' && <EmailDomainsTab navigateTo={navigateTo} />}
      {tab === 'tiers' && <SpendingTiersTab navigateTo={navigateTo} />}
      {tab === 'tags' && <CustomTagsTab navigateTo={navigateTo} user={user} />}
      {tab === 'shared' && <SharedAccountsTab navigateTo={navigateTo} />}
    </div>
  );
}

// ─── Tab 1: Celebrities review + prune ─────────────────────────────────────────
function CelebritiesTab({ navigateTo }) {
  // status maps to review_action column:
  //   confirmed = admin explicitly kept or promoted
  //   demoted   = admin marked low-confidence
  //   rejected  = admin dismissed
  //   pending   = never reviewed (review_action IS NULL)
  //   all       = no filter
  const [filters, setFilters] = useState({ category: '', confidence: '', state: '', status: 'confirmed', q: '' });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [noteEdit, setNoteEdit] = useState({ id: null, value: '' });
  const [showAdd, setShowAdd] = useState(false);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { limit: LIMIT, offset };
      if (filters.category) p.category = filters.category;
      if (filters.confidence) p.confidence = filters.confidence;
      if (filters.state) p.state = filters.state;
      if (filters.status && filters.status !== 'all') p.review_action = filters.status;
      if (filters.q.trim()) p.q = filters.q.trim();
      const data = await postNeon('celebrity_candidates_list', p);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const flag = async (id, action, note) => {
    setBusyId(id + ':' + action);
    try {
      await postNeon('celebrity_candidate_flag', { id, action, note });
      await load();
    } catch (e) {
      alert(`Flag failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const saveNote = async (id) => {
    if (!noteEdit.value.trim()) { setNoteEdit({ id: null, value: '' }); return; }
    await flag(id, 'confirm', noteEdit.value.trim());
    setNoteEdit({ id: null, value: '' });
  };

  const onFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setOffset(0); };

  return (
    <div>
      <div className="card" style={{ padding: 12, marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <SelectFilter value={filters.category} onChange={v => onFilter('category', v)} label="Category"
          options={[['', 'All categories'], ['actor','actor'],['actress','actress'],['athlete','athlete'],['musician','musician'],['politician','politician'],['business','business'],['tv','tv'],['author','author'],['journalist','journalist'],['filmmaker','filmmaker'],['fashion','fashion'],['screenwriter','screenwriter'],['other','other']]} />
        <SelectFilter value={filters.confidence} onChange={v => onFilter('confidence', v)} label="Confidence"
          options={[['', 'All levels'], ['high','high'],['medium','medium'],['low','low']]} />
        <input type="text" value={filters.state} onChange={e => onFilter('state', e.target.value.toUpperCase().slice(0, 2))}
          placeholder="State (MD)" style={{ width: 80, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
        <SelectFilter value={filters.status} onChange={v => onFilter('status', v)} label="Status"
          options={[['confirmed', 'Confirmed'], ['pending', 'Pending'], ['demoted', 'Demoted'], ['rejected', 'Rejected'], ['all', 'All']]} />
        <input type="text" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { setOffset(0); load(); } }}
          placeholder="Search name/email/reason..."
          style={{ flex: 1, minWidth: 180, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
        <button onClick={() => { setOffset(0); load(); }} className="btn btn-navy" style={{ fontSize: 12 }}>
          <Search size={14} style={{ verticalAlign: -3 }} /> Search
        </button>
        <button onClick={load} className="btn" style={{ fontSize: 12, border: '1px solid var(--gray-300)' }}>
          <RefreshCw size={14} style={{ verticalAlign: -3 }} /> Refresh
        </button>
        <button onClick={() => setShowAdd(s => !s)} className="btn btn-primary" style={{ fontSize: 12 }}>
          <Plus size={14} style={{ verticalAlign: -3 }} /> Add manually
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gray-500)' }}>
          {total.toLocaleString()} {total === 1 ? 'match' : 'matches'}
        </span>
      </div>

      {showAdd && <AddCelebrityPanel onDone={() => { setShowAdd(false); load(); }} onCancel={() => setShowAdd(false)} />}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--gray-50)' }}>
            <tr>
              <Th>Name</Th><Th>Email</Th><Th>State</Th><Th right>LTV</Th><Th right>Home $</Th>
              <Th>Cat</Th><Th>Conf</Th><Th>Reason</Th><Th>Caveat</Th><Th>Status</Th><Th center>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>No matches.</td></tr>
            )}
            {rows.map(r => {
              const rowKey = r.id ?? r.contactid;
              const isEditingNote = noteEdit.id === rowKey;
              return (
                <tr key={rowKey} style={{ borderTop: '1px solid var(--gray-100)', opacity: r.rejected ? 0.55 : 1 }}>
                  <Td>
                    <a href="#" onClick={e => { e.preventDefault(); navigateTo?.('customer-search', r.contactid); }}
                       style={{ color: 'var(--navy)', textDecoration: 'none', fontWeight: 500 }}>{r.name || '—'}</a>
                  </Td>
                  <Td><span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{r.email || '—'}</span></Td>
                  <Td>{r.state || '—'}</Td>
                  <Td right>{fmtMoney(r.lifetime_value)}</Td>
                  <Td right>
                    {r.on_shared_account
                      ? <span style={{ color: 'var(--gray-400)', fontSize: 11 }} title="Customer account is a CS catch-all — home value reflects account holder, not this person.">n/a</span>
                      : r.home_value != null
                        ? <span title={r.shipping_home_value != null ? `Ship-to: ${fmtMoney(r.shipping_home_value)}` : undefined}>{fmtMoney(r.home_value)}</span>
                        : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                  </Td>
                  <Td><Pill color={CAT_COLORS[r.category] || CAT_COLORS.other}>{r.category || 'other'}</Pill></Td>
                  <Td><Pill color={CONFIDENCE_COLORS[r.confidence] || '#6b7280'}>{r.confidence}</Pill></Td>
                  <Td><span style={{ fontSize: 11 }}>{r.reason}</span></Td>
                  <Td><span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--gray-500)' }}>{r.caveat || '—'}</span></Td>
                  <Td>
                    {r.review_action === 'rejected' ? <Pill color="#b91c1c">rejected</Pill>
                     : r.review_action === 'demoted' ? <Pill color="#ca8a04">demoted</Pill>
                     : r.review_action === 'confirmed' ? <Pill color="#16a34a">confirmed</Pill>
                     : r.rejected ? <Pill color="#b91c1c">rejected</Pill>
                     : r.reviewed ? <Pill color="#16a34a">reviewed</Pill>
                     : <Pill color="#9ca3af">pending</Pill>}
                    {r.reviewer_note && !isEditingNote && (
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>“{r.reviewer_note}”</div>
                    )}
                    {isEditingNote && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <input type="text" value={noteEdit.value} autoFocus
                          onChange={e => setNoteEdit({ ...noteEdit, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') saveNote(r.contactid); if (e.key === 'Escape') setNoteEdit({ id: null, value: '' }); }}
                          placeholder="Note..." style={{ fontSize: 11, padding: '2px 4px', border: '1px solid var(--gray-300)', borderRadius: 4, width: 120 }} />
                        <button onClick={() => saveNote(rowKey)} style={btnIconStyle}><Check size={12} /></button>
                      </div>
                    )}
                  </Td>
                  <Td center>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      {!r.rejected && (
                        <IconBtn title="Confirm" onClick={() => flag(rowKey, 'confirm')} color="#16a34a" busy={busyId === r.contactid + ':confirm'}><Check size={13} /></IconBtn>
                      )}
                      {!r.rejected && r.confidence !== 'low' && (
                        <IconBtn title="Demote to low" onClick={() => flag(rowKey, 'demote')} color="#ca8a04" busy={busyId === r.contactid + ':demote'}><ArrowDown size={13} /></IconBtn>
                      )}
                      {!r.rejected && r.confidence !== 'high' && (
                        <IconBtn title="Promote to high" onClick={() => flag(rowKey, 'promote')} color="#0ea5e9" busy={busyId === r.contactid + ':promote'}><ArrowUp size={13} /></IconBtn>
                      )}
                      {!r.rejected && (
                        <IconBtn title="Reject" onClick={() => flag(rowKey, 'reject')} color="#b91c1c" busy={busyId === r.contactid + ':reject'}><X size={13} /></IconBtn>
                      )}
                      {r.rejected && (
                        <IconBtn title="Un-reject" onClick={() => flag(rowKey, 'unreview')} color="#6b7280" busy={busyId === r.contactid + ':unreview'}><RefreshCw size={13} /></IconBtn>
                      )}
                      <IconBtn title="Edit note" onClick={() => setNoteEdit({ id: rowKey, value: r.reviewer_note || '' })} color="#6b7280"><Edit2 size={13} /></IconBtn>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, fontSize: 12 }}>
        <div>Showing {offset + 1}-{Math.min(offset + LIMIT, total)} of {total.toLocaleString()}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0} className="btn" style={{ fontSize: 12, border: '1px solid var(--gray-300)' }}>‹ Prev</button>
          <button onClick={() => setOffset(offset + LIMIT)} disabled={offset + LIMIT >= total} className="btn" style={{ fontSize: 12, border: '1px solid var(--gray-300)' }}>Next ›</button>
        </div>
      </div>
    </div>
  );
}

// ─── Add celebrity manually (used inside Celebrities tab) ─────────────────────
function AddCelebrityPanel({ onDone, onCancel }) {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [category, setCategory] = useState('other');
  const [confidence, setConfidence] = useState('high');
  const [reason, setReason] = useState('');
  const [caveat, setCaveat] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const doSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const data = await postNeon('customer_metrics_search', { q: q.trim() });
      setResults(data.rows || []);
    } catch (e) {
      setError(e.message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const save = async () => {
    if (!picked) {
      setError('Pick a customer first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const billToContext = picked.source === 'bill_to'
        ? ` (call-in bill-to on ${Number(picked.bill_to_total_orders || 0).toLocaleString()} orders across ${Number(picked.bill_to_distinct_customers || 0)} account(s); tagged to ${[picked.firstname, picked.lastname].filter(Boolean).join(' ') || picked.email || 'customer'})`
        : '';
      await postNeon('celebrity_candidate_add', {
        contactid: picked.contactid,
        category, confidence,
        reason: reason.trim() || `Manually added by admin (${category})${billToContext}`,
        caveat: caveat.trim() || null,
        name_override: picked.source === 'bill_to' ? picked.name : null,
        note: picked.source === 'bill_to' ? `Call-in / bill-to match. Name "${picked.name}" on ${picked.bill_to_total_orders} orders; tagged to customer account ${picked.contactid}.` : null,
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 14, marginBottom: 10, border: '1px solid #fed7aa', background: '#fff7ed' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Add notable customer manually</div>
        <button onClick={onCancel} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--gray-500)' }}><X size={16} /></button>
      </div>

      {!picked ? (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 6 }}>Step 1 — find the customer</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input type="text" value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Search by real name / last name / email (e.g. Hicks, driesell, jlegend@)"
              style={{ flex: 1, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
            <button onClick={doSearch} disabled={searching || !q.trim()} className="btn btn-navy" style={{ fontSize: 12 }}>
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>
            Celebs often register under their <strong>legal name</strong> (Mo'nique → "Monique Hicks") or via an assistant. If a stage name doesn't match, try just the last name or a distinctive email substring. Results rank by how many of your search words matched.
          </div>
          {results.length > 0 && (
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, background: 'white' }}>
              {results.map((r, i) => {
                const isBillTo = r.source === 'bill_to';
                const accountName = [r.firstname, r.lastname].filter(Boolean).join(' ') || r.email || `Customer ${r.contactid}`;
                return (
                  <div key={`${r.contactid}-${r.source}-${i}`}
                    onClick={() => setPicked(r)}
                    style={{ padding: '8px 10px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>{r.name || '(no name)'}</span>
                        <Pill color={isBillTo ? '#ea580c' : '#0ea5e9'}>{isBillTo ? 'bill-to name' : 'customer'}</Pill>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                        {isBillTo ? (
                          <>
                            {r.state || '—'}{r.city ? ' · ' + r.city : ''} · {Number(r.bill_to_total_orders || 0).toLocaleString()} orders across {Number(r.bill_to_distinct_customers || 0)} account(s) · primary: {accountName}{r.email ? ' (' + r.email + ')' : ''}
                          </>
                        ) : (
                          <>{r.email} · {r.state || '—'}{r.city ? ' · ' + r.city : ''}</>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11 }}>
                      <div>{fmtMoney(r.lifetime_value)} LTV</div>
                      <div style={{ color: 'var(--gray-500)' }}>{Number(r.order_count || 0).toLocaleString()} orders</div>
                      {!isBillTo && r.home_value != null && (
                        <div style={{ color: 'var(--gray-500)' }} title={r.shipping_home_value != null ? `Ship-to home: ${fmtMoney(r.shipping_home_value)}` : undefined}>
                          🏠 {fmtMoney(r.home_value)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!searching && q && results.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 8 }}>No matches. Try another search.</div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 6 }}>Step 2 — classify</div>
          <div style={{ padding: 10, background: 'white', borderRadius: 6, border: '1px solid var(--gray-200)', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{picked.name}</span>
                <Pill color={picked.source === 'bill_to' ? '#ea580c' : '#0ea5e9'}>{picked.source === 'bill_to' ? 'bill-to name' : 'customer'}</Pill>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                {picked.source === 'bill_to' ? (
                  <>
                    Will tag on customer account <strong>{[picked.firstname, picked.lastname].filter(Boolean).join(' ') || picked.email || picked.contactid}</strong>
                    {picked.email ? ' (' + picked.email + ')' : ''} · Name "{picked.name}" appears on {Number(picked.bill_to_total_orders || 0).toLocaleString()} orders across {Number(picked.bill_to_distinct_customers || 0)} account(s)
                  </>
                ) : (
                  <>{picked.email} · {picked.state || '—'} · {fmtMoney(picked.lifetime_value)} LTV · {Number(picked.order_count || 0).toLocaleString()} orders</>
                )}
              </div>
            </div>
            <button onClick={() => setPicked(null)} className="btn" style={{ fontSize: 11, border: '1px solid var(--gray-300)', marginLeft: 8 }}>Change</button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
              Category
              <select value={category} onChange={e => setCategory(e.target.value)}
                style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }}>
                {['actor','actress','athlete','musician','politician','business','tv','author','journalist','filmmaker','fashion','screenwriter','other'].map(c =>
                  <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
              Confidence
              <select value={confidence} onChange={e => setConfidence(e.target.value)}
                style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }}>
                <option value="high">high</option>
                <option value="medium">medium</option>
                <option value="low">low</option>
              </select>
            </label>
          </div>

          <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
            Reason <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>(optional — who is this; shows to Jimmy + in the table)</span>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              placeholder='e.g., "Pulitzer-winning journalist for Washington Post"'
              rows={2}
              style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6, resize: 'vertical' }} />
          </label>

          <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 }}>
            Caveat <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>(optional — e.g., "common name, but email confirms")</span>
            <input type="text" value={caveat} onChange={e => setCaveat(e.target.value)}
              style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
          </label>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onCancel} className="btn" style={{ fontSize: 12, border: '1px solid var(--gray-300)' }}>Cancel</button>
            <button onClick={save} disabled={saving} className="btn btn-primary" style={{ fontSize: 12, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Add to list'}
            </button>
          </div>
        </>
      )}

      {error && <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>{error}</div>}
    </div>
  );
}

// ─── Tab 2: Email Domains ──────────────────────────────────────────────────────
function EmailDomainsTab({ navigateTo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [drilldown, setDrilldown] = useState({});
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await postNeon('customer_email_domains', { min_count: 2, limit: 500 });
        setRows((data.rows || []).map(r => ({ ...r, type: classifyDomain(r.domain) })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (typeFilter && r.type !== typeFilter) return false;
      if (needle && !r.domain.includes(needle)) return false;
      return true;
    });
  }, [rows, q, typeFilter]);

  const toggleExpand = async (domain) => {
    if (expanded === domain) { setExpanded(null); return; }
    setExpanded(domain);
    if (!drilldown[domain]) {
      try {
        const data = await postNeon('customer_email_domain_drilldown', { domain });
        setDrilldown(d => ({ ...d, [domain]: data.rows || [] }));
      } catch (e) {
        setDrilldown(d => ({ ...d, [domain]: [] }));
      }
    }
  };

  // Type summary chips
  const typeSummary = useMemo(() => {
    const m = {};
    rows.forEach(r => { m[r.type] = (m[r.type] || 0) + r.customer_count; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <div>
      <div className="card" style={{ padding: 12, marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Filter domains (e.g. nfl, .gov, washingtonpost)"
          style={{ flex: 1, minWidth: 200, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
        <SelectFilter value={typeFilter} onChange={setTypeFilter} label="Type"
          options={[['', 'All types'], ['corporate','corporate'],['government','government'],['military','military'],['education','education'],['media','media'],['nonprofit','nonprofit'],['personal','personal'],['other','other']]} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {typeSummary.map(([type, n]) => (
          <button key={type} onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
            style={{
              border: 'none', padding: '4px 10px', borderRadius: 12, fontSize: 11,
              background: typeFilter === type ? (DOMAIN_TYPE_COLORS[type] || '#6b7280') : '#f3f4f6',
              color: typeFilter === type ? 'white' : '#374151', cursor: 'pointer',
            }}>
            {type} <strong>{n.toLocaleString()}</strong>
          </button>
        ))}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--gray-50)' }}>
            <tr><Th></Th><Th>Domain</Th><Th>Type</Th><Th right>Customers</Th><Th right>Total LTV</Th><Th right>Avg LTV</Th><Th right>Active VIPs</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>No domains match.</td></tr>}
            {filtered.map(r => (
              <React.Fragment key={r.domain}>
                <tr onClick={() => toggleExpand(r.domain)} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                  <Td>{expanded === r.domain ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</Td>
                  <Td><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.domain}</span></Td>
                  <Td><Pill color={DOMAIN_TYPE_COLORS[r.type] || '#6b7280'}>{r.type}</Pill></Td>
                  <Td right>{Number(r.customer_count).toLocaleString()}</Td>
                  <Td right>{fmtMoney(r.total_ltv)}</Td>
                  <Td right>{fmtMoney(r.avg_ltv)}</Td>
                  <Td right>{Number(r.active_vips).toLocaleString()}</Td>
                </tr>
                {expanded === r.domain && (
                  <tr><td colSpan={7} style={{ padding: 0, background: 'var(--gray-50)' }}>
                    <DomainDrilldown rows={drilldown[r.domain]} navigateTo={navigateTo} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DomainDrilldown({ rows, navigateTo }) {
  if (!rows) return <div style={{ padding: 14, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading...</div>;
  if (rows.length === 0) return <div style={{ padding: 14, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>No customers.</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
      <thead style={{ background: 'var(--gray-100)' }}>
        <tr><Th>Name</Th><Th>Email</Th><Th>State</Th><Th right>LTV</Th><Th right>Orders</Th><Th>Last</Th><Th>VIP</Th></tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.contactid} style={{ borderTop: '1px solid var(--gray-200)' }}>
            <Td><a href="#" onClick={e => { e.preventDefault(); navigateTo?.('customer-search', r.contactid); }}
               style={{ color: 'var(--navy)', textDecoration: 'none' }}>{[r.firstname, r.lastname].filter(Boolean).join(' ') || '—'}</a></Td>
            <Td>{r.email}</Td>
            <Td>{r.state}</Td>
            <Td right>{fmtMoney(r.lifetime_value)}</Td>
            <Td right>{Number(r.order_count).toLocaleString()}</Td>
            <Td>{fmtDate(r.last_order_date)}</Td>
            <Td>{r.vip_status === 'active' ? 'Active' : r.vip_status === 'lapsed' ? 'Lapsed' : '—'}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Tab 3: Spending tiers ────────────────────────────────────────────────────
const TIERS = [
  { key: 'tier_10k_plus', label: '$10,000+',       min: 10000, max: null,  revKey: 'revenue_10k_plus' },
  { key: 'tier_5k_10k',   label: '$5,000 – $10,000', min: 5000,  max: 10000, revKey: 'revenue_5k_10k' },
  { key: 'tier_1k_5k',    label: '$1,000 – $5,000',  min: 1000,  max: 5000,  revKey: 'revenue_1k_5k' },
  { key: 'tier_500_1k',   label: '$500 – $1,000',    min: 500,   max: 1000,  revKey: 'revenue_500_1k' },
  { key: 'tier_100_500',  label: '$100 – $500',      min: 100,   max: 500,   revKey: 'revenue_100_500' },
  { key: 'tier_under_100',label: 'Under $100',       min: 0.01,  max: 100,   revKey: 'revenue_under_100' },
  { key: 'tier_zero',     label: 'No orders',        min: null,  max: null,  revKey: null },
];

function SpendingTiersTab({ navigateTo }) {
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [drilldown, setDrilldown] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const data = await postNeon('customer_spending_tiers');
        setSummary(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const toggle = async (tier) => {
    if (expanded === tier.key) { setExpanded(null); return; }
    if (tier.min == null) return; // no drilldown for "no orders"
    setExpanded(tier.key);
    if (!drilldown[tier.key]) {
      const p = { min_ltv: tier.min };
      if (tier.max != null) p.max_ltv = tier.max;
      try {
        const data = await postNeon('customer_spending_tier_drilldown', p);
        setDrilldown(d => ({ ...d, [tier.key]: data.rows || [] }));
      } catch (e) {
        setDrilldown(d => ({ ...d, [tier.key]: [] }));
      }
    }
  };

  if (!summary) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>;

  const totalCustomers = TIERS.reduce((s, t) => s + Number(summary[t.key] || 0), 0);

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead style={{ background: 'var(--gray-50)' }}>
          <tr><Th></Th><Th>Tier</Th><Th right>Customers</Th><Th right>% of base</Th><Th right>Revenue</Th></tr>
        </thead>
        <tbody>
          {TIERS.map(t => {
            const count = Number(summary[t.key] || 0);
            const pct = totalCustomers > 0 ? (count / totalCustomers * 100).toFixed(1) : 0;
            const rev = t.revKey ? summary[t.revKey] : null;
            const clickable = t.min != null;
            return (
              <React.Fragment key={t.key}>
                <tr onClick={() => clickable && toggle(t)}
                    style={{ borderTop: '1px solid var(--gray-100)', cursor: clickable ? 'pointer' : 'default' }}>
                  <Td>{clickable ? (expanded === t.key ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : null}</Td>
                  <Td><strong>{t.label}</strong></Td>
                  <Td right>{count.toLocaleString()}</Td>
                  <Td right>{pct}%</Td>
                  <Td right>{rev != null ? fmtMoney(rev) : '—'}</Td>
                </tr>
                {expanded === t.key && (
                  <tr><td colSpan={5} style={{ padding: 0, background: 'var(--gray-50)' }}>
                    <DomainDrilldown rows={drilldown[t.key]} navigateTo={navigateTo} />
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab 4: Custom tags ───────────────────────────────────────────────────────
function CustomTagsTab({ navigateTo, user }) {
  const [tags, setTags] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [tagRows, setTagRows] = useState({});
  const [newTagCustomer, setNewTagCustomer] = useState('');
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [initedTable, setInitedTable] = useState(false);

  const loadTags = useCallback(async () => {
    try {
      if (!initedTable) {
        await postNeon('customer_tags_init');
        setInitedTable(true);
      }
      const data = await postNeon('customer_tags_cloud');
      setTags(data.rows || []);
    } catch (e) {
      console.error(e);
    }
  }, [initedTable]);

  useEffect(() => { loadTags(); }, [loadTags]);

  const toggle = async (tag) => {
    if (expanded === tag) { setExpanded(null); return; }
    setExpanded(tag);
    if (!tagRows[tag]) {
      try {
        const data = await postNeon('customer_tags_by_tag', { tag });
        setTagRows(r => ({ ...r, [tag]: data.rows || [] }));
      } catch (e) {
        setTagRows(r => ({ ...r, [tag]: [] }));
      }
    }
  };

  const addTag = async () => {
    if (!newTagCustomer.trim() || !newTag.trim()) return;
    setBusy(true);
    try {
      await postNeon('customer_tags_add', {
        contactid: newTagCustomer.trim(),
        tag: newTag.trim(),
        created_by: user?.email || user?.username || 'admin',
      });
      setNewTagCustomer(''); setNewTag('');
      if (expanded === newTag.trim()) setTagRows(r => { const copy = { ...r }; delete copy[newTag.trim()]; return copy; });
      await loadTags();
    } catch (e) {
      alert(`Add failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const removeTag = async (contactid, tag) => {
    if (!window.confirm(`Remove tag "${tag}" from customer ${contactid}?`)) return;
    try {
      await postNeon('customer_tags_remove', { contactid, tag });
      setTagRows(r => { const copy = { ...r }; delete copy[tag]; return copy; });
      await loadTags();
    } catch (e) {
      alert(`Remove failed: ${e.message}`);
    }
  };

  return (
    <div>
      <div className="card" style={{ padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Add tag to customer</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" value={newTagCustomer} onChange={e => setNewTagCustomer(e.target.value)}
            placeholder="Customer contactid (find via Customer Search)"
            style={{ flex: '1 1 280px', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
          <input type="text" value={newTag} onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTag(); }}
            placeholder="Tag (e.g. VIP whale, Bulk reseller)"
            style={{ flex: '1 1 200px', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
          <button onClick={addTag} disabled={busy || !newTagCustomer.trim() || !newTag.trim()}
            className="btn btn-primary" style={{ fontSize: 12 }}>
            <Plus size={13} style={{ verticalAlign: -2 }} /> Add tag
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
          Tip: look up a customer on Customer Search, their contactid shows in the URL or details panel.
        </div>
      </div>

      {tags.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>
          No tags yet. Add one above to get started.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: 'var(--gray-50)' }}>
              <tr><Th></Th><Th>Tag</Th><Th right>Customers</Th><Th right>Total LTV</Th><Th right>Active VIPs</Th></tr>
            </thead>
            <tbody>
              {tags.map(t => (
                <React.Fragment key={t.tag}>
                  <tr onClick={() => toggle(t.tag)} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                    <Td>{expanded === t.tag ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</Td>
                    <Td><strong>{t.tag}</strong></Td>
                    <Td right>{Number(t.customer_count).toLocaleString()}</Td>
                    <Td right>{fmtMoney(t.total_ltv)}</Td>
                    <Td right>{Number(t.active_vips).toLocaleString()}</Td>
                  </tr>
                  {expanded === t.tag && (
                    <tr><td colSpan={5} style={{ padding: 0, background: 'var(--gray-50)' }}>
                      <TagDrilldown rows={tagRows[t.tag]} tag={t.tag} navigateTo={navigateTo} onRemove={removeTag} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TagDrilldown({ rows, tag, navigateTo, onRemove }) {
  if (!rows) return <div style={{ padding: 14, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading...</div>;
  if (rows.length === 0) return <div style={{ padding: 14, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>No customers.</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
      <thead style={{ background: 'var(--gray-100)' }}>
        <tr><Th>Name</Th><Th>Email</Th><Th>State</Th><Th right>LTV</Th><Th right>Orders</Th><Th>Added</Th><Th center></Th></tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.contactid} style={{ borderTop: '1px solid var(--gray-200)' }}>
            <Td><a href="#" onClick={e => { e.preventDefault(); navigateTo?.('customer-search', r.contactid); }}
               style={{ color: 'var(--navy)', textDecoration: 'none' }}>{[r.firstname, r.lastname].filter(Boolean).join(' ') || '—'}</a></Td>
            <Td>{r.email}</Td>
            <Td>{r.state}</Td>
            <Td right>{fmtMoney(r.lifetime_value)}</Td>
            <Td right>{Number(r.order_count).toLocaleString()}</Td>
            <Td><span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmtDate(r.created_at)}{r.created_by ? ` · ${r.created_by}` : ''}</span></Td>
            <Td center>
              <IconBtn title="Remove tag" onClick={() => onRemove(r.contactid, tag)} color="#b91c1c"><Trash2 size={12} /></IconBtn>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Tab 5: Shared / catch-all accounts ──────────────────────────────────────
function SharedAccountsTab({ navigateTo }) {
  const [shared, setShared] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newId, setNewId] = useState('');
  const [newReason, setNewReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [retrofitMsg, setRetrofitMsg] = useState(null);
  const [inited, setInited] = useState(false);

  const load = useCallback(async () => {
    try {
      if (!inited) {
        await postNeon('shared_accounts_init');
        setInited(true);
      }
      const [sa, cands] = await Promise.all([
        postNeon('shared_accounts_list'),
        postNeon('shared_accounts_candidates'),
      ]);
      setShared(sa.rows || []);
      setCandidates(cands.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [inited]);

  useEffect(() => { load(); }, [load]);

  const addShared = async (contactid, reason) => {
    if (!contactid) return;
    setBusy(true);
    try {
      await postNeon('shared_accounts_add', { contactid: String(contactid), reason: reason || null });
      setNewId(''); setNewReason('');
      await load();
    } catch (e) { alert(`Add failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const removeShared = async (contactid) => {
    if (!window.confirm(`Remove shared-account flag for ${contactid}? Celebrity rows pinned to this account will keep their current LTV until the next retrofit.`)) return;
    try {
      await postNeon('shared_accounts_remove', { contactid });
      await load();
    } catch (e) { alert(`Remove failed: ${e.message}`); }
  };

  const retrofit = async () => {
    setBusy(true);
    setRetrofitMsg(null);
    try {
      const r = await postNeon('celebrity_candidates_retrofit_shared');
      setRetrofitMsg(`Retrofit complete — recomputed LTV on ${r.updated || 0} celebrity row${r.updated === 1 ? '' : 's'}.`);
    } catch (e) {
      setRetrofitMsg(`Retrofit failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--gray-600)', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: 10, marginBottom: 10 }}>
        <strong>Why this matters:</strong> some customer accounts aggregate orders from <em>many</em> different real customers — CS catch-alls, legacy dumping grounds, call-in desks. Their LTV is the sum of many people's spending, so a celebrity pinned to one of those accounts inherits an inflated number. Flagging the account here tells the celebrity-LTV logic to recompute each name's real spend from the bill-to records.
      </div>

      {/* Current shared accounts */}
      <div className="card" style={{ marginBottom: 10, overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Flagged shared accounts ({shared.length})</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {retrofitMsg && <span style={{ fontSize: 11, color: 'var(--gray-600)' }}>{retrofitMsg}</span>}
            <button onClick={retrofit} disabled={busy || shared.length === 0} className="btn btn-navy" style={{ fontSize: 11 }}>
              {busy ? 'Working...' : 'Re-run retrofit'}
            </button>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--gray-50)' }}>
            <tr><Th>Contact ID</Th><Th>Account name</Th><Th>Email</Th><Th right>Orders</Th><Th right>Aggregate LTV</Th><Th>Reason</Th><Th center></Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</td></tr>}
            {!loading && shared.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--gray-400)' }}>No shared accounts flagged yet.</td></tr>}
            {shared.map(r => (
              <tr key={r.contactid} style={{ borderTop: '1px solid var(--gray-100)' }}>
                <Td><a href="#" onClick={e => { e.preventDefault(); navigateTo?.('customer-search', r.contactid); }} style={{ color: 'var(--navy)', textDecoration: 'none' }}>{r.contactid}</a></Td>
                <Td>{[r.firstname, r.lastname].filter(Boolean).join(' ') || '—'}</Td>
                <Td><span style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.email || '—'}</span></Td>
                <Td right>{Number(r.order_count || 0).toLocaleString()}</Td>
                <Td right>{fmtMoney(r.lifetime_value)}</Td>
                <Td><span style={{ fontSize: 11 }}>{r.reason || '—'}</span></Td>
                <Td center><IconBtn title="Remove" onClick={() => removeShared(r.contactid)} color="#b91c1c"><Trash2 size={12} /></IconBtn></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add new */}
      <div className="card" style={{ padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Flag a shared account manually</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" value={newId} onChange={e => setNewId(e.target.value)}
            placeholder="Contact ID (e.g. 575)"
            style={{ flex: '0 1 160px', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
          <input type="text" value={newReason} onChange={e => setNewReason(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addShared(newId, newReason); }}
            placeholder="Reason (e.g. 'CS catch-all for call-ins')"
            style={{ flex: '1 1 240px', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
          <button onClick={() => addShared(newId, newReason)} disabled={busy || !newId.trim()} className="btn btn-primary" style={{ fontSize: 12 }}>
            <Plus size={13} style={{ verticalAlign: -2 }} /> Flag
          </button>
        </div>
      </div>

      {/* Candidate catch-alls */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', fontSize: 13, fontWeight: 500 }}>
          Candidate catch-alls ({candidates.length})
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 8 }}>— accounts with suspicious emails or unusually high order counts</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--gray-50)' }}>
            <tr><Th>Contact ID</Th><Th>Account name</Th><Th>Email</Th><Th right>Orders</Th><Th right>Aggregate LTV</Th><Th>Why flagged</Th><Th center></Th></tr>
          </thead>
          <tbody>
            {candidates.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--gray-400)' }}>No candidates — either all flagged or none match heuristics.</td></tr>}
            {candidates.map(r => (
              <tr key={r.contactid} style={{ borderTop: '1px solid var(--gray-100)' }}>
                <Td><a href="#" onClick={e => { e.preventDefault(); navigateTo?.('customer-search', r.contactid); }} style={{ color: 'var(--navy)', textDecoration: 'none' }}>{r.contactid}</a></Td>
                <Td>{[r.firstname, r.lastname].filter(Boolean).join(' ') || '—'}</Td>
                <Td><span style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.email || '—'}</span></Td>
                <Td right>{Number(r.order_count || 0).toLocaleString()}</Td>
                <Td right>{fmtMoney(r.lifetime_value)}</Td>
                <Td><span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{r.suggested_reason || 'high volume'}</span></Td>
                <Td center>
                  <button onClick={() => addShared(r.contactid, r.suggested_reason || 'Flagged from candidate list')} disabled={busy}
                    className="btn" style={{ fontSize: 11, border: '1px solid var(--gray-300)', padding: '2px 8px' }}>
                    Flag as shared
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Shared small components ──────────────────────────────────────────────────
function Th({ children, right, center }) {
  return <th style={{ padding: '6px 10px', textAlign: right ? 'right' : center ? 'center' : 'left', fontWeight: 500, fontSize: 11, color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Td({ children, right, center }) {
  return <td style={{ padding: '6px 10px', textAlign: right ? 'right' : center ? 'center' : 'left', whiteSpace: right ? 'nowrap' : undefined, verticalAlign: 'top' }}>{children}</td>;
}
function Pill({ color, children }) {
  return <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, background: color + '22', color, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.3 }}>{children}</span>;
}
const btnIconStyle = { border: 'none', background: 'transparent', padding: 2, cursor: 'pointer', color: 'var(--gray-500)' };
function IconBtn({ children, color, onClick, title, busy }) {
  return (
    <button onClick={onClick} title={title} disabled={busy}
      style={{ border: 'none', background: 'transparent', padding: 3, borderRadius: 4, cursor: busy ? 'wait' : 'pointer', color, opacity: busy ? 0.4 : 1 }}>
      {children}
    </button>
  );
}
function SelectFilter({ label, value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={label}
      style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 6, background: 'white' }}>
      {options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
    </select>
  );
}
